'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { Users, HeartPulse, BrainCircuit, Scale, Sparkles } from 'lucide-react';

// --- Constants ---
const COLORS = {
  DARK_BG: '#000000',
  ACCENT_GOLD: '#faaf40',
};

// Updated text colors for better contrast against dark backgrounds
const TEXT_MUTED = 'text-zinc-300';
const TEXT_HEADER = 'text-white';

const values = [
  {
    title: 'THE TRIBE',
    subtitle: 'Community First',
    icon: Users,
    description: 'We are not a room of strangers. We are a collective. At Aura Wellness Clubs, ego is left at the door, and every member is a partner in the journey toward greatness.',
  },
  {
    title: 'THE ENGINE',
    subtitle: 'Vitality Over Vanity',
    icon: HeartPulse,
    description: 'We train for life, not just for mirrors. Our focus is on metabolic health, cellular recovery, and building a body that performs as beautifully as it looks.',
  },
  {
    title: 'THE METHOD',
    subtitle: 'Data, Not Guesses',
    icon: BrainCircuit,
    description: 'We respect the science of the human body. Every workout and recovery protocol at Aura Wellness Clubs is backed by evidence and tracked for progress.',
  },
  {
    title: 'THE BALANCE',
    subtitle: 'Yin & Yang',
    icon: Scale,
    description: 'Intensity requires tranquility. We believe that true power comes from the ability to switch between high-output training and deep, restorative calm.',
  },
];

export default function OurValuesPremium() {
  return (
    <section className="relative w-full py-16 md:py-24 px-4 sm:px-6 lg:px-8 overflow-hidden bg-black">
      
      {/* --- BACKGROUND LAYERS --- */}
      {/* 1. The Gradient: Deepened opacity slightly for text contrast */}
      <div
        className="absolute inset-0 bg-gradient-to-br transition-transform duration-1000"
        style={{
          background: `linear-gradient(to bottom right, ${COLORS.DARK_BG}, #1a1a1a, #402313, ${COLORS.ACCENT_GOLD}, #d98d4a, #a56330, #1a1a1a, ${COLORS.DARK_BG})`,
          opacity: 0.9,
        }}
      />

      {/* 2. Dark Tint: Essential for readability */}
      <div className="absolute inset-0 bg-black/70 pointer-events-none" />

      {/* 3. Noise Texture: Adds the "film grain" premium feel */}
      <div 
        className="absolute inset-0 opacity-[0.04] pointer-events-none mix-blend-overlay" 
        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }} 
      />

      <div className="max-w-7xl mx-auto relative z-10">
        
        {/* --- HEADER --- */}
        <div className="text-center mb-16 md:mb-20 px-2">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full border border-[#faaf40]/30 bg-[#faaf40]/10 backdrop-blur-md"
          >
            <Sparkles className="w-3 h-3 md:w-4 md:h-4 text-[#faaf40]" />
            <span className="text-[10px] md:text-xs font-bold tracking-[0.2em] uppercase text-[#faaf40]">
              The Foundation
            </span>
          </motion.div>
          
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tight text-balance"
          >
            The Principles That <br className="hidden md:block" /> Guide <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#faaf40] to-[#d98d4a]">Aura Wellness Clubs.</span>
          </motion.h2>
        </div>

        {/* --- CARDS GRID --- */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 lg:gap-8">
          {values.map((item, idx) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: idx * 0.1, duration: 0.5, ease: "easeOut" }}
              
              // Card Styling
              className="group relative h-full flex flex-col p-6 md:p-8 rounded-3xl border border-white/5 bg-white/[0.03] backdrop-blur-sm overflow-hidden transition-all duration-500 hover:bg-white/[0.06] hover:border-[#faaf40]/30"
            >
              
              {/* Hover Light Sweep Effect */}
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent -translate-x-[200%] group-hover:translate-x-[200%] transition-transform duration-[1s] ease-in-out pointer-events-none z-20" />

              {/* Decorative Background Icon (Watermark) */}
              {/* aria-hidden ensures screen readers ignore this visual decoration */}
              <item.icon 
                aria-hidden="true"
                className="absolute -bottom-8 -right-8 md:-bottom-12 md:-right-12 w-32 h-32 md:w-56 md:h-56 text-black/40 group-hover:text-[#faaf40]/5 group-hover:-rotate-12 transition-all duration-700 ease-out pointer-events-none z-0" 
                strokeWidth={0.5}
              />

              {/* Card Content Layer */}
              <div className="relative z-10 flex flex-col h-full">
                
                {/* Header: Icon + Tag */}
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6 md:mb-8">
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl border border-white/10 bg-black/40 flex items-center justify-center group-hover:bg-[#faaf40] group-hover:border-[#faaf40] transition-colors duration-300 shadow-lg shrink-0">
                    <item.icon className="w-5 h-5 md:w-6 md:h-6 text-gray-300 group-hover:text-black transition-colors duration-300" />
                  </div>
                  
                  <span className="self-start text-[10px] font-bold text-[#faaf40]/90 tracking-[0.2em] uppercase py-1 px-2.5 rounded border border-[#faaf40]/20 bg-black/40 backdrop-blur-md">
                    {item.subtitle}
                  </span>
                </div>

                {/* Body Text */}
                <div className="mt-auto">
                  <h3 className={`text-xl md:text-2xl font-black ${TEXT_HEADER} uppercase leading-none mb-3 md:mb-4 drop-shadow-sm`}>
                    {item.title}
                  </h3>
                  <p className={`text-sm md:text-base ${TEXT_MUTED} leading-relaxed font-light group-hover:text-gray-100 transition-colors`}>
                    {item.description}
                  </p>
                </div>
              </div>

            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
}